'use client';

import { useCallback, useEffect, useMemo } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { toast } from '@/shared/components/toast';
import { COPY } from '@/shared/constants/copies';
import { QUERY_KEY_INITIALS } from '@/shared/constants/query-key-initials';
import { useAppStore } from '@/shared/contexts/app-wrapper/providers/app-provider';
import {
	changeExtraCredits,
	fetchExtraCredits,
	previewExtraCreditsChange,
} from '@/shared/services/payment';
import { ExtraCreditsTier, PlanType } from '@/shared/services/payment/payment.types';
import { captureCustomSentryError } from '@/shared/utils/sentry-utils';
import { redirectViaWindow } from '@/shared/utils/window';

import { GET_CURRENT_PLAN_QUERY_KEY } from '@/utils/constants';

/**
 * The extra-credits catalog for a workspace, optionally for a plan it is about to buy.
 *
 * The catalog comes from the API and is rendered as-is, rather than filtered against a
 * hardcoded per-plan table, so a change to the tier line-up needs no frontend edit.
 */
export function useExtraCredits({ planType }: { planType?: PlanType } = {}) {
	const selectedWorkspaceId = useAppStore((state) => state.selectedWorkspaceId);

	const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
		queryKey: [QUERY_KEY_INITIALS.EXTRA_CREDITS, selectedWorkspaceId, planType],
		queryFn: () =>
			fetchExtraCredits({ page_id: selectedWorkspaceId ?? undefined, plan_type: planType }),
		enabled: !!selectedWorkspaceId,
	});

	// A failing extra-credits catalog silently blocks every purchase path, so it is reported
	// rather than swallowed into an empty list.
	useEffect(() => {
		if (!isError) return;

		captureCustomSentryError(error, '[Billing] Extra-credits catalog failed to load', {
			severity: 'high',
			tags: { error_category: 'extra_credits_load_failed' },
			fingerprint: ['extra-credits-fetch-failed'],
		});
	}, [isError, error]);

	// Plan-level eligibility is derived from the plan, not from `can_purchase`: that
	// flag also goes false when the card has lapsed or the subscription is not ACTIVE,
	// so trusting it here would tell a paying Business customer that extra credits require a
	// paid plan. `canPurchase` is passed through separately to disable the action.
	const eligible = planType !== undefined && planType !== PlanType.FREE;

	return useMemo(
		() => ({
			extraCredits: data?.extraCredits ?? [],
			eligible,
			canPurchase: data?.canPurchase ?? false,
			currentTier: data?.currentExtraCreditsTier ?? null,
			currency: data?.currency,
			isLoading,
			isError,
			isFetching,
			refetch,
		}),
		[data, eligible, isLoading, isError, isFetching, refetch]
	);
}

/**
 * The exact amount raising extra credits to `tier` would charge now. Fetched only while the
 * confirm step is open, and refetched each time it opens: the yearly amount depends on the date.
 */
export function useExtraCreditsChargePreview({
	tier,
	enabled,
}: {
	tier: ExtraCreditsTier | null;
	enabled: boolean;
}) {
	const selectedWorkspaceId = useAppStore((state) => state.selectedWorkspaceId);

	const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
		queryKey: [QUERY_KEY_INITIALS.EXTRA_CREDITS_CHARGE_PREVIEW, selectedWorkspaceId, tier],
		queryFn: () =>
			previewExtraCreditsChange({
				page_id: selectedWorkspaceId ?? undefined,
				add_on_tier: tier,
			}),
		enabled: enabled && !!selectedWorkspaceId && tier !== null,
		gcTime: 0,
	});

	// Without the amount the admin cannot pay, so a failure here blocks the increase.
	useEffect(() => {
		if (!isError) return;

		captureCustomSentryError(error, '[Billing] Extra-credits charge preview failed to load', {
			severity: 'high',
			tags: { error_category: 'extra_credits_preview_failed' },
			fingerprint: ['extra-credits-charge-preview-failed'],
		});
	}, [isError, error]);

	return { preview: data, isLoading, isError, isFetching, refetch };
}

/**
 * Add, change or remove the workspace's single extra-credits tier (`tier: null` removes).
 *
 * Extra-credit changes are impossible in the Stripe Customer Portal, so this is the only
 * path a customer has.
 */
export function useChangeExtraCredits() {
	const selectedWorkspaceId = useAppStore((state) => state.selectedWorkspaceId);
	const queryClient = useQueryClient();

	const { mutateAsync, isPending } = useMutation({
		mutationFn: (tier: ExtraCreditsTier | null) =>
			changeExtraCredits({ page_id: selectedWorkspaceId ?? undefined, add_on_tier: tier }),
		onSuccess: (response) => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEY_INITIALS.EXTRA_CREDITS] });
			queryClient.invalidateQueries({ queryKey: [GET_CURRENT_PLAN_QUERY_KEY] });

			if (response.status === 'APPLIED') {
				// A removal applies with a null tier — it is a success, not a payment
				// failure, so it must not fall through to PAYMENT_REQUIRED below.
				toast({
					title:
						response.currentExtraCreditsTier === null
							? COPY.billing.extraCreditsRemoved
							: COPY.billing.extraCreditsApplied(response.currentExtraCreditsTier),
					variant: 'success',
				});
				return;
			}

			if (response.status === 'SCHEDULED' && response.pendingEffectiveAt) {
				toast({
					title: COPY.billing.extraCreditsScheduled(
						response.pendingExtraCreditsTier,
						response.pendingEffectiveAt
					),
				});
				return;
			}

			// PAYMENT_REQUIRED: entitlements are unchanged until the user completes
			// payment at Stripe. Redirect in the same tab, like the plan upgrade flow —
			// a popup here is blocked often enough to strand the purchase.
			if (response.paymentUrl) {
				toast({ title: COPY.billing.extraCreditsPaymentRequired });
				redirectViaWindow(response.paymentUrl);
				return;
			}

			toast({ title: COPY.billing.extraCreditsPaymentUrlMissing, variant: 'destructive' });
		},
		onError: () => {
			toast({ title: COPY.billing.extraCreditsFailed, variant: 'destructive' });
		},
	});

	const changeTier = useCallback((tier: ExtraCreditsTier | null) => mutateAsync(tier), [mutateAsync]);

	return { changeTier, isChanging: isPending };
}
